/**
 * Ordered overlay stack — TASK 7.2 Step 3 (mock-defect fix 1 + 2).
 *
 * The mock kept FOUR independent booleans — `sheet.open`, `cm.open`,
 * `im.open`, `ddm.open` (L847) — plus a fifth implicit overlay, the full
 * player, which was a ROUTE (`/player`, L730). Nothing coordinated them:
 * ui-contract §10.1 measured the consequences at runtime and recorded
 * "**Escape does not dismiss any overlay**" and "**focus is not trapped** in
 * most modals". Both are fixed here, without changing a pixel: this module
 * owns ORDER, DISMISSAL and FOCUS; the overlay bodies (./overlays.js) own
 * markup.
 *
 * ── ORDER AND Z-INDEX ─────────────────────────────────────────────────────
 * {@link OVERLAY_LAYERS} carries the base layer of each overlay kind, verbatim
 * from ui-contract §8.6 / tokens.css (`--sm-z-player: 60` … `--sm-z-delete-data: 85`).
 * Those bases encode the USUAL nesting, but they cannot encode every one: AC
 * 0.4's own example is the full player (base 60) opened OVER a confirm modal
 * (base 80). A stack whose top is painted underneath its parent is not a
 * stack, so each entry's EFFECTIVE z-index is
 *
 *     max(baseLayer, previousTop.effectiveZ + 1)
 *
 * assigned with CSSOM — and only when it must be raised, so the ordinary
 * single-overlay case keeps the exact value its class declares and the
 * fidelity baseline is untouched.
 *
 * ── HISTORY / `popstate` (AC 0.4) ─────────────────────────────────────────
 * "Back dismisses the topmost overlay BEFORE any route navigation" needs the
 * overlay to own a history entry, but the app is HASH-routed: a naive
 * `pushState('#/overlay')` would change the visible route and fire
 * `hashchange`. So opening an overlay pushes a SENTINEL entry at the SAME URL
 * (`location.href` unchanged) whose `history.state` records the stack DEPTH:
 *
 *     history.pushState({ __smOverlayDepth: n }, '', location.href)
 *
 * Same URL => no `hashchange`, no visible route change, no router work.
 * On `popstate` the handler reads the depth of the entry it landed on and
 * reconciles: `while (stack.length > targetDepth) pop()`. That is depth
 * RECONCILIATION rather than a "pending back" counter, which matters because
 * `history.go(-n)` fires a single `popstate` for n entries — a counter would
 * desynchronise, the depth never does.
 *
 * Consequences, all tested:
 *   - one back with player-over-modal pops ONLY the player; the modal stays
 *     and the URL (hence the route) is unchanged;
 *   - a programmatic close (Escape, Cancel) pops the overlay and then calls
 *     `history.back()` to CONSUME its sentinel, so the next hardware back is
 *     a real navigation and never a swallowed no-op;
 *   - the consumed sentinel leaves a forward entry at the same URL. It is
 *     inert (same URL, and `stack.length > targetDepth` is false so nothing
 *     pops) and the next real `location.hash =` assignment truncates it.
 *
 * ── FOCUS ─────────────────────────────────────────────────────────────────
 * Trap on Tab/Shift+Tab in every overlay including the full player; the
 * empty-focusable case parks focus on the dialog itself, the single-focusable
 * case re-focuses that one control. Each entry remembers ITS OWN invoker, so
 * unwinding several levels restores focus level by level and ends on the
 * control that opened the bottom-most overlay.
 *
 * @module
 */

/**
 * Base z-index per overlay kind — ui-contract §8.6, mirrored from tokens.css
 * (`--sm-z-player`, `--sm-z-sheet`, `--sm-z-modal`, `--sm-z-delete-data`).
 * apps/web/test/overlay-stack.test.ts asserts these stay in step with the
 * tokens, so a token edit cannot silently desynchronise them.
 */
export const OVERLAY_LAYERS = Object.freeze({
  player: 60,
  sheet: 70,
  confirm: 80,
  input: 80,
  deleteData: 85,
});

/** `history.state` key holding the overlay depth of a sentinel entry. */
export const OVERLAY_DEPTH_KEY = '__smOverlayDepth';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'summary',
  'audio[controls]',
  'video[controls]',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * @param {Element} root
 * @returns {HTMLElement[]}
 */
function focusablesIn(root) {
  return [...root.querySelectorAll(FOCUSABLE_SELECTOR)].filter((node) => {
    if (node.hasAttribute('hidden')) return false;
    if (node.getAttribute('aria-hidden') === 'true') return false;
    if (node.closest('[inert]') !== null) return false;
    return true;
  });
}

/**
 * @typedef {object} OverlayDescriptor
 * @property {keyof typeof OVERLAY_LAYERS} kind
 * @property {HTMLElement} element the outermost node (scrim, or the full
 *   player's own fixed-inset container) that gets appended to the overlay host
 * @property {HTMLElement} dialog the node carrying `role="dialog"` +
 *   `aria-modal="true"` + an accessible name. May be `element` itself.
 * @property {HTMLElement} [initialFocus] control to focus on open; defaults to
 *   the first focusable descendant, then the dialog itself
 * @property {() => void} [onDismiss] called after the overlay leaves the stack
 */

/**
 * @typedef {OverlayDescriptor & {
 *   effectiveZ: number,
 *   invoker: HTMLElement|null,

 * }} OverlayEntry
 */

/**
 * @typedef {object} OverlayStackHandle
 * @property {(descriptor: OverlayDescriptor) => OverlayEntry} open
 * @property {(options?: { then?: () => void }) => OverlayEntry|null} closeTop
 * @property {(options?: { then?: () => void }) => number} closeAll
 * @property {() => number} closeAllSilently tear down without touching history
 *   (used when a real route change has already superseded the overlays)
 * @property {() => OverlayEntry|null} top
 * @property {() => number} size
 * @property {() => readonly OverlayEntry[]} entries
 * @property {() => void} dispose
 */

/**
 * Creates the stack manager.
 *
 * @param {{
 *   host: HTMLElement,
 *   background: HTMLElement,
 *   doc?: Document,
 *   win?: Window,
 * }} options
 *   `host` is where overlay elements mount (the mock appends them at the end
 *   of the shell); `background` is the subtree made inert while any overlay is
 *   open (header + main + nav).
 * @returns {OverlayStackHandle}
 */
export function createOverlayStack(options) {
  const { host, background, doc = document, win = window } = options;

  /** @type {OverlayEntry[]} */
  const stack = [];
  /**
   * FIFO of callbacks queued by programmatic closes, drained one per
   * `popstate` — the seam that lets "close this sheet, THEN navigate" run
   * after the history traversal instead of racing it.
   *
   * @type {Array<(() => void)|null>}
   */
  const pendingAfterPop = [];
  let disposed = false;

  /** @param {Element} node */
  function setInert(node) {
    node.setAttribute('inert', '');
    node.setAttribute('aria-hidden', 'true');
  }

  /** @param {Element} node */
  function clearInert(node) {
    node.removeAttribute('inert');
    node.removeAttribute('aria-hidden');
  }

  /** @param {OverlayEntry} entry */
  function focusInto(entry) {
    const target =
      entry.initialFocus ??
      focusablesIn(entry.element)[0] ??
      /** @type {HTMLElement} */ (entry.dialog);
    if (target === entry.dialog && !entry.dialog.hasAttribute('tabindex')) {
      entry.dialog.setAttribute('tabindex', '-1');
    }
    target.focus({ preventScroll: true });
  }

  /** @returns {number} the depth recorded on the CURRENT history entry. */
  function currentDepth() {
    // jsdom hands back `undefined` (not null) for an entry with no state.
    const state = /** @type {Record<string, unknown>|null|undefined} */ (win.history.state);
    const depth = state === null || state === undefined ? 0 : state[OVERLAY_DEPTH_KEY];
    return typeof depth === 'number' ? depth : 0;
  }

  /**
   * Removes the top entry from the DOM and restores its invoker's focus.
   * Does NOT touch history — every caller decides that.
   *
   * @returns {OverlayEntry|null}
   */
  function teardownTop() {
    const entry = stack.pop();
    if (entry === undefined) return null;

    entry.element.remove();

    const nextTop = stack[stack.length - 1];
    if (nextTop === undefined) {
      clearInert(background);
    } else {
      clearInert(nextTop.element);
    }

    if (entry.invoker !== null && entry.invoker.isConnected) {
      entry.invoker.focus({ preventScroll: true });
    } else if (nextTop !== undefined) {
      focusInto(nextTop);
    }

    if (entry.onDismiss !== undefined) entry.onDismiss();
    return entry;
  }

  /** @param {OverlayDescriptor} descriptor */
  function open(descriptor) {
    const { kind, element, dialog } = descriptor;
    const base = OVERLAY_LAYERS[kind];
    if (base === undefined) throw new Error(`OverlayStack: unknown overlay kind "${String(kind)}"`);
    if (
      dialog.getAttribute('aria-label') === null &&
      dialog.getAttribute('aria-labelledby') === null
    ) {
      throw new Error(`OverlayStack: the "${kind}" dialog has no accessible name`);
    }

    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');

    const previousTop = stack[stack.length - 1];
    const effectiveZ =
      previousTop === undefined ? base : Math.max(base, previousTop.effectiveZ + 1);
    // Only override the class-declared z-index when the stack forces a raise —
    // the ordinary single-overlay case keeps the mock's exact value.
    if (effectiveZ !== base) element.style.setProperty('z-index', String(effectiveZ));

    if (previousTop === undefined) {
      setInert(background);
    } else {
      setInert(previousTop.element);
    }

    /** @type {OverlayEntry} */
    const entry = {
      ...descriptor,
      effectiveZ,
      invoker: /** @type {HTMLElement|null} */ (
        doc.activeElement instanceof win.HTMLElement ? doc.activeElement : null
      ),
    };

    host.append(element);
    stack.push(entry);

    // Sentinel history entry — SAME URL, so no hashchange and no route change.
    win.history.pushState(
      {
        .../** @type {object|null|undefined} */ (win.history.state ?? {}),
        [OVERLAY_DEPTH_KEY]: stack.length,
      },
      '',
      win.location.href,
    );

    focusInto(entry);
    return entry;
  }

  /** @param {{ then?: () => void }} [opts] */
  function closeTop(opts = {}) {
    const entry = teardownTop();
    if (entry === null) return null;
    // Consume this overlay's sentinel entry so the NEXT hardware back is a
    // real navigation. `then` runs after the traversal lands (see below).
    pendingAfterPop.push(opts.then ?? null);
    win.history.back();
    return entry;
  }

  /** @param {{ then?: () => void }} [opts] */
  function closeAll(opts = {}) {
    const count = stack.length;
    if (count === 0) {
      if (opts.then !== undefined) opts.then();
      return 0;
    }
    while (stack.length > 0) teardownTop();
    pendingAfterPop.push(opts.then ?? null);
    // One popstate for the whole traversal — depth reconciliation copes.
    win.history.go(-count);
    return count;
  }

  function closeAllSilently() {
    const count = stack.length;
    while (stack.length > 0) teardownTop();
    return count;
  }

  function handlePopState() {
    const target = currentDepth();
    // AC 0.4: dismiss the topmost overlay BEFORE any route navigation. Because
    // the sentinel carries the same URL, no route navigation is even pending.
    while (stack.length > target) teardownTop();
    const after = pendingAfterPop.shift();
    if (after !== undefined && after !== null) after();
  }

  /** @param {KeyboardEvent} event */
  function handleKeydown(event) {
    const entry = stack[stack.length - 1];
    if (entry === undefined) return;

    if (event.key === 'Escape') {
      // ONE Escape = ONE pop. The listener is single and document-level, so
      // a nested stack cannot cascade.
      event.preventDefault();
      event.stopPropagation();
      closeTop();
      return;
    }

    if (event.key !== 'Tab') return;

    const items = focusablesIn(entry.element);
    if (items.length === 0) {
      // Nothing to move to — hold focus on the dialog itself.
      event.preventDefault();
      if (!entry.dialog.hasAttribute('tabindex')) entry.dialog.setAttribute('tabindex', '-1');
      entry.dialog.focus({ preventScroll: true });
      return;
    }

    const first = /** @type {HTMLElement} */ (items[0]);
    const last = /** @type {HTMLElement} */ (items[items.length - 1]);

    if (items.length === 1) {
      event.preventDefault();
      first.focus({ preventScroll: true });
      return;
    }

    const active = doc.activeElement;
    if (active === null || !entry.element.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus({ preventScroll: true });
      return;
    }
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
      return;
    }
    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  doc.addEventListener('keydown', handleKeydown, true);
  win.addEventListener('popstate', handlePopState);

  return {
    open,
    closeTop,
    closeAll,
    closeAllSilently,
    top: () => stack[stack.length - 1] ?? null,
    size: () => stack.length,
    entries: () => [...stack],
    dispose() {
      if (disposed) return;
      disposed = true;
      closeAllSilently();
      doc.removeEventListener('keydown', handleKeydown, true);
      win.removeEventListener('popstate', handlePopState);
    },
  };
}
